import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/auth/Login";
import AdminLogin from "@/pages/auth/AdminLogin";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import LandingPage from "@/pages/LandingPage";
import DPHome from "@/droppartner/Home";
import {
  ShopPage, CategoriesPage, DealsPage, ProductPage, CartPage, HowItWorksPage,
  PartnerPlansPage, BecomePartnerPage, TrackOrderPage, AboutPage, ContactPage,
  FAQPage, HelpCenterPage, BlogPage as DPBlogPage, BlogPostPage, LegalPage,
} from "@/droppartner/pages";

import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminCategories from "@/pages/admin/Categories";
import AdminAgents from "@/pages/admin/Agents";
import AdminAgentDetail from "@/pages/admin/AgentDetail";
import AdminInventory from "@/pages/admin/Inventory";
import AdminShipping from "@/pages/admin/Shipping";
import AdminReturns from "@/pages/admin/Returns";
import AdminAccounting from "@/pages/admin/Accounting";
import AdminReports from "@/pages/admin/Reports";
import AdminIntegrations from "@/pages/admin/Integrations";
import AdminAudit from "@/pages/admin/Audit";
import AdminSettings from "@/pages/admin/Settings";
import AdminPlans from "@/pages/admin/Plans";
import AdminWithdrawals from "@/pages/admin/Withdrawals";
import AdminMarketplace from "@/pages/admin/Marketplace";
import AdminDomains from "@/pages/admin/Domains";
import AdminTemplates from "@/pages/admin/Templates";
import InvoicePage from "@/pages/InvoicePage";

import AgentLayout from "@/layouts/AgentLayout";
import AgentDashboard from "@/pages/agent/Dashboard";
import AgentProducts from "@/pages/agent/Products";
import AgentStore from "@/pages/agent/Store";
import AgentShipments from "@/pages/agent/Shipments";
import AgentWallet from "@/pages/agent/Wallet";
import AgentKYC from "@/pages/agent/KYC";
import AIStudio from "@/pages/agent/AIStudio";
import AgentBasket from "@/pages/agent/Basket";
import AgentAnalytics from "@/pages/agent/Analytics";
import AgentCustomers from "@/pages/agent/Customers";
import AgentPlan from "@/pages/agent/Plan";
import AgentSettings from "@/pages/agent/Settings";
import AgentMarketplace from "@/pages/agent/Marketplace";
import Support from "@/pages/agent/Support";

import OrdersPage from "@/pages/shared/OrdersPage";
import Storefront from "@/pages/storefront/Storefront";

function Protected({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-[#090D16] text-sm text-slate-500" data-testid="auth-loading">Loading…</div>;
  }
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "agent" ? "/agent" : "/admin"} replace />;
  }
  return children;
}

function HostGate({ children }) {
  const loc = useLocation();
  const appHost = new URL(process.env.REACT_APP_BACKEND_URL).hostname;
  const host = window.location.hostname;
  const custom = !["localhost", "127.0.0.1", appHost].includes(host);
  const passThrough = !custom || loc.pathname.startsWith("/store/");
  const [state, setState] = useState(passThrough ? "pass" : "checking");

  useEffect(() => {
    if (passThrough) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/host-store?host=${host}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        // Custom-domain mode: render the SAME storefront in place — no redirect,
        // the browser keeps the agent's domain (window.__CUSTOM_STORE_SLUG__ drives it).
        window.__CUSTOM_STORE_SLUG__ = d.slug;
        window.__CUSTOM_STORE_PRIMARY__ = d.primary_domain || "";
        setState("custom");
      })
      .catch(() => setState("none"));
  }, []);

  if (passThrough || state === "pass") return children;
  if (state === "custom") {
    return (
      <Routes>
        <Route path="*" element={<Storefront />} />
      </Routes>
    );
  }
  if (state === "none") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 text-center" data-testid="host-not-found">
        <h1 className="text-xl font-bold text-slate-700">No store connected to this domain</h1>
        <p className="mt-1 text-sm text-slate-500">If you just set this up, DNS changes can take up to 24–48 hours.</p>
      </div>
    );
  }
  return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500" data-testid="host-resolving">Opening store…</div>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <HostGate>
        <Routes>
          {/* DropPartner public website */}
          <Route path="/" element={<DPHome />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/deals" element={<DealsPage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/partner-plans" element={<PartnerPlansPage />} />
          <Route path="/become-a-partner" element={<BecomePartnerPage />} />
          <Route path="/track-order" element={<TrackOrderPage />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/contact-us" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/help-center" element={<HelpCenterPage />} />
          <Route path="/blog" element={<DPBlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/terms-and-conditions" element={<LegalPage kind="terms-and-conditions" />} />
          <Route path="/privacy-policy" element={<LegalPage kind="privacy-policy" />} />
          <Route path="/return-policy" element={<LegalPage kind="return-policy" />} />
          <Route path="/shipping-policy" element={<LegalPage kind="shipping-policy" />} />
          <Route path="/cancellation-policy" element={<LegalPage kind="cancellation-policy" />} />
          {/* internal app */}
          <Route path="/login" element={<Login />} />
          <Route path="/ai-admin" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />

          <Route path="/admin" element={<Protected roles={["super_admin", "admin"]}><AdminLayout /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="agents/:id" element={<AdminAgentDetail />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="orders" element={<OrdersPage scope="admin" />} />
            <Route path="shipping" element={<AdminShipping />} />
            <Route path="returns" element={<AdminReturns scope="admin" />} />
            <Route path="accounting" element={<AdminAccounting />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="integrations" element={<AdminIntegrations />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="marketplace" element={<AdminMarketplace />} />
            <Route path="domains" element={<AdminDomains />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="withdrawals" element={<AdminWithdrawals />} />
            <Route path="support" element={<Support scope="admin" />} />
          </Route>

          <Route path="/agent" element={<Protected roles={["agent"]}><AgentLayout /></Protected>}>
            <Route index element={<AgentDashboard />} />
            <Route path="products" element={<AgentProducts />} />
            <Route path="store" element={<AgentStore />} />
            <Route path="builder" element={<Navigate to="/agent/store" replace />} />
            <Route path="marketplace" element={<AgentMarketplace />} />
            <Route path="ai-studio" element={<AIStudio />} />
            <Route path="orders" element={<OrdersPage scope="agent" />} />
            <Route path="shipments" element={<AgentShipments />} />
            <Route path="returns" element={<AdminReturns scope="agent" />} />
            <Route path="wallet" element={<AgentWallet />} />
            <Route path="kyc" element={<AgentKYC />} />
            <Route path="basket" element={<AgentBasket />} />
            <Route path="analytics" element={<AgentAnalytics />} />
            <Route path="customers" element={<AgentCustomers />} />
            <Route path="support" element={<Support />} />
            <Route path="plan" element={<AgentPlan />} />
            <Route path="settings" element={<AgentSettings />} />
          </Route>

          <Route path="/invoice/:oid/:type" element={<Protected><InvoicePage /></Protected>} />
          <Route path="/store/:slug/*" element={<Storefront />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </HostGate>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" richColors />
    </AuthProvider>
  );
}

export default App;
